import { graphql } from 'react-relay';
import { useMemo, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';

import {
	SearchConfluenceDocumentStatus,
	type useContentTreeSearchContentTreeSearchQuery as useContentTreeSearchContentTreeSearchQuery$data,
	type useContentTreeSearchContentTreeSearchQueryVariables as useContentTreeSearchContentTreeSearchQuery$variables,
} from './__types__/useContentTreeSearchContentTreeSearchQuery.ts';

const SEARCH_PAGE_SIZE = 50;
const CONTENT_TREE_SEARCH_ENTITY_ATIS = [
	'ati:cloud:confluence:page',
	'ati:cloud:confluence:whiteboard',
	'ati:cloud:confluence:database',
	'ati:cloud:confluence:embed',
	'ati:cloud:confluence:slide',
];
const CONTENT_TREE_SEARCH_ENTITY_ATIS_WITH_FOLDERS = [
	...CONTENT_TREE_SEARCH_ENTITY_ATIS,
	'ati:cloud:confluence:folder',
];

const isFolderEntityId = (id: unknown) => typeof id === 'string' && id.includes(':folder/');

const getSearchResultType = ({ id, entityId, type, confluenceEntity }: any): string | undefined => {
	if (
		confluenceEntity?.__typename === 'ConfluenceFolder' ||
		isFolderEntityId(entityId) ||
		isFolderEntityId(id)
	) {
		return 'folder';
	}

	return type;
};

const transformSearchData = (data?: useContentTreeSearchContentTreeSearchQuery$data) => {
	const nodes = data?.search?.search?.edges?.map((edge) => edge.node) || undefined;
	return nodes?.map(({ id, entityId, title, type, confluenceEntity }: any) => ({
		id: entityId,
		title,
		displayEmoji: confluenceEntity?.metadata?.titleEmojiPublished?.value,
		type: getSearchResultType({ id, entityId, type, confluenceEntity }),
		subType: confluenceEntity?.subtype?.toLowerCase() ?? '',
		// TODO: Show super admins which pages are restricted -- this info is not yet available in AGG
		hasRestrictions: false,
		hasInheritedRestrictions: false,
		// TODO: DISCO-774 Add draft support to SpaceViewsList
	}));
};

type ContentTreeSearchSource = 'sideNavigation' | 'moveDialog';

type ContentTreeSearchItem = {
	id: any;
	title: any;
	displayEmoji: any;
	type: any;
	subType: any;
	hasRestrictions: boolean;
	hasInheritedRestrictions: boolean;
};

type ContentTreeSearchResult = {
	searchResult?: ContentTreeSearchItem[];
	loading: boolean;
	error?: ApolloErrorInterop;
};

export const useContentTreeSearch = (
	searchText: string,
	source: ContentTreeSearchSource,
	spaceKey?: string,
	includeFolders = false,
): ContentTreeSearchResult => {
	const [pageSpaceKey] = usePageSpaceKey();
	const resolvedSpaceKey = spaceKey ?? pageSpaceKey; // Use provided spaceKey or fall back to context
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId(resolvedSpaceKey);
	const entities = includeFolders
		? CONTENT_TREE_SEARCH_ENTITY_ATIS_WITH_FOLDERS
		: CONTENT_TREE_SEARCH_ENTITY_ATIS;

	const { data, loading, error } = useQueryInterop<
		useContentTreeSearchContentTreeSearchQuery$data,
		useContentTreeSearchContentTreeSearchQuery$variables
	>(
		graphql`
			query useContentTreeSearchContentTreeSearchQuery(
				$experience: String!
				$searchText: String!
				$first: Int
				$filters: SearchFilterInput!
			) {
				search {
					search(experience: $experience, query: $searchText, first: $first, filters: $filters) {
						edges {
							node {
								id
								title
								type
								... on SearchConfluencePageBlogAttachment {
									entityId
									confluenceEntity {
										... on ConfluencePage {
											id
											subtype
											metadata {
												titleEmojiPublished {
													id
													value
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				searchText,
				experience: 'confluence.searchInTree',
				first: SEARCH_PAGE_SIZE,
				filters: {
					entities,
					locations: ['ari:cloud:confluence::site/'.concat(cloudId)],
					confluenceFilters: {
						spacesFilter: resolvedSpaceKey ? [resolvedSpaceKey] : undefined,
						contentStatuses: [SearchConfluenceDocumentStatus.CURRENT],
						titleMatchOnly: true,
					},
				},
			},
			interopApolloAggClient: true,
			skip: searchText.length === 0,
		},
	);

	const searchResult = useMemo(() => transformSearchData(data), [data]);

	useEffect(() => {
		if (searchResult) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'searched',
					actionSubject: 'contentTree',
					source,
					containerId: spaceId,
					containerType: 'space',
					attributes: {
						searchResultCount: searchResult.length,
						navdexPointType: TOUCH,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, searchResult, spaceId, source]);

	return {
		searchResult,
		loading,
		error,
	};
};
